import React, { useState, useEffect } from 'react';
import AuthorFields from '../components/AuthorFields';
import CitationResult from '../components/CitationResult';
import { formatAuthorsForCitation, formatAuthorsForParenthetical, formatAuthorsForReference, formatURL } from '../utils/citationHelpers';

function PodcastForm() {
  const [hosts, setHosts] = useState([]);
  const [hostType, setHostType] = useState('personal');
  const [formData, setFormData] = useState({
    episodeTitle: '',
    podcastTitle: '',
    podcastType: 'Audio',
    platform: '',
    year: '',
    day: '',
    month: '',
    url: ''
  });
  const [result, setResult] = useState(null);
  const [liveResult, setLiveResult] = useState(null);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Genera una vista previa en tiempo real
  useEffect(() => {
    if (hosts.length > 0 && formData.episodeTitle && formData.podcastTitle && formData.year) {
      generatePreview();
    } else {
      setLiveResult(null);
    }
  }, [hosts, formData]);

  const formatDate = () => {
    const { year, month, day } = formData;
    if (!year) return '';

    if (day && month) {
      const monthNames = {
        '01': 'enero', '02': 'febrero', '03': 'marzo', '04': 'abril',
        '05': 'mayo', '06': 'junio', '07': 'julio', '08': 'agosto',
        '09': 'septiembre', '10': 'octubre', '11': 'noviembre', '12': 'diciembre'
      };
      return `${year}, ${parseInt(day)} de ${monthNames[month]}`;
    } else if (month) {
      const monthNames = {
        '01': 'enero', '02': 'febrero', '03': 'marzo', '04': 'abril',
        '05': 'mayo', '06': 'junio', '07': 'julio', '08': 'agosto',
        '09': 'septiembre', '10': 'octubre', '11': 'noviembre', '12': 'diciembre'
      };
      return `${year}, ${monthNames[month]}`;
    }
    return year;
  };

  const getHostLabel = (hosts, hostType) => {
    if (hostType === 'institutional') {
      return hosts.length > 1 ? '[Anfitriones]' : '[Anfitrión]';
    }

    if (hosts.length === 0) return '[Anfitrión]';
    const lastHost = hosts[hosts.length - 1];
    const isAllFeminine = hosts.every(h => h.gender === 'feminine');

    if (hosts.length === 1) {
      return lastHost.gender === 'feminine' ? '[Anfitriona]' : '[Anfitrión]';
    } else {
      if (isAllFeminine) {
        return '[Anfitrionas]';
      } else {
        return '[Anfitriones]';
      }
    }
  };

  const getPodcastTypeLabel = () => {
    return formData.podcastType === 'Video' ? '[Episodio de pódcast de video]' : '[Episodio de pódcast de audio]';
  };

  const generatePreview = () => {
    // Verificar si hay autores institucionales con siglas
    const institutionalAuthors = hosts.filter(
      author => author.type === 'institutional' && author.abbreviation
    );

    let narrativeCitation, parentheticalCitation, narrativeSubsequent, parentheticalSubsequent;

    if (institutionalAuthors.length === 1 && (hostType === 'institutional' || hostType === 'both')) {
      // Para cita narrativa con institucional con sigla: "Nombre (SIGLA, año)"
      narrativeCitation = `${institutionalAuthors[0].name} (${institutionalAuthors[0].abbreviation}, ${formData.year})`;
      
      // Para cita parentética: "(Nombre [SIGLA], año)"
      parentheticalCitation = `(${institutionalAuthors[0].name} [${institutionalAuthors[0].abbreviation}], ${formData.year})`;
      
      // Para cita narrativa subsecuente: "SIGLA (año)"
      narrativeSubsequent = `${institutionalAuthors[0].abbreviation} (${formData.year})`;
      
      // Para cita parentética subsecuente: "(SIGLA, año)"
      parentheticalSubsequent = `(${institutionalAuthors[0].abbreviation}, ${formData.year})`;
    } else {
      // Caso normal sin autor institucional con sigla o múltiples autores
      const hostsForCitation = formatAuthorsForCitation(hosts, hostType);
      const hostsForParenthetical = formatAuthorsForParenthetical(hosts, hostType);
      const hostsForCitationSubsequent = formatAuthorsForCitation(hosts, hostType, false);
      const hostsForParentheticalSubsequent = formatAuthorsForParenthetical(hosts, hostType, false);

      narrativeCitation = `${hostsForCitation} (${formData.year})`;
      parentheticalCitation = `(${hostsForParenthetical}, ${formData.year})`;
      narrativeSubsequent = `${hostsForCitationSubsequent} (${formData.year})`;
      parentheticalSubsequent = `(${hostsForParentheticalSubsequent}, ${formData.year})`;
    }

    const citation = {
      narrative: narrativeCitation,
      parenthetical: parentheticalCitation,
      narrativeSubsequent: narrativeSubsequent,
      parentheticalSubsequent: parentheticalSubsequent
    };

    const authorsForReference = formatAuthorsForReference(hosts, hostType);
    const hostLabel = getHostLabel(hosts, hostType);
    const dateFormatted = formatDate();
    const url = formatURL(formData.url);
    const podcastTypeLabel = getPodcastTypeLabel();

    let referenceText = `${authorsForReference} (${dateFormatted}). ${formData.episodeTitle} ${podcastTypeLabel}. En <em>${formData.podcastTitle}</em>`;
    
    if (formData.platform) {
      referenceText += `. ${formData.platform}`;
    }
    
    if (url) {
      referenceText += `. ${url}`;
    } else {
      referenceText += '.';
    }

    const reference = {
      html: referenceText,
      text: referenceText.replace(/<[^>]*>/g, '')
    };

    setLiveResult({ citation, reference });
  };

  const generateCitation = () => {
    if (hosts.length === 0 || !formData.episodeTitle || !formData.podcastTitle || !formData.year) {
      alert('Por favor completa todos los campos obligatorios');
      return;
    }

    // Verificar si hay autores institucionales con siglas
    const institutionalAuthors = hosts.filter(
      author => author.type === 'institutional' && author.abbreviation
    );

    let narrativeCitation, parentheticalCitation, narrativeSubsequent, parentheticalSubsequent;

    if (institutionalAuthors.length === 1 && (hostType === 'institutional' || hostType === 'both')) {
      // Para cita narrativa con institucional con sigla: "Nombre (SIGLA, año)"
      narrativeCitation = `${institutionalAuthors[0].name} (${institutionalAuthors[0].abbreviation}, ${formData.year})`;
      
      // Para cita parentética: "(Nombre [SIGLA], año)"
      parentheticalCitation = `(${institutionalAuthors[0].name} [${institutionalAuthors[0].abbreviation}], ${formData.year})`;
      
      // Para cita narrativa subsecuente: "SIGLA (año)"
      narrativeSubsequent = `${institutionalAuthors[0].abbreviation} (${formData.year})`;
      
      // Para cita parentética subsecuente: "(SIGLA, año)"
      parentheticalSubsequent = `(${institutionalAuthors[0].abbreviation}, ${formData.year})`;
    } else {
      // Caso normal sin autor institucional con sigla o múltiples autores
      const hostsForCitation = formatAuthorsForCitation(hosts, hostType);
      const hostsForParenthetical = formatAuthorsForParenthetical(hosts, hostType);
      const hostsForCitationSubsequent = formatAuthorsForCitation(hosts, hostType, false);
      const hostsForParentheticalSubsequent = formatAuthorsForParenthetical(hosts, hostType, false);

      narrativeCitation = `${hostsForCitation} (${formData.year})`;
      parentheticalCitation = `(${hostsForParenthetical}, ${formData.year})`;
      narrativeSubsequent = `${hostsForCitationSubsequent} (${formData.year})`;
      parentheticalSubsequent = `(${hostsForParentheticalSubsequent}, ${formData.year})`;
    }

    const citation = {
      narrative: narrativeCitation,
      parenthetical: parentheticalCitation,
      narrativeSubsequent: narrativeSubsequent,
      parentheticalSubsequent: parentheticalSubsequent
    };

    const authorsForReference = formatAuthorsForReference(hosts, hostType);
    const hostLabel = getHostLabel(hosts, hostType);
    const dateFormatted = formatDate();
    const url = formatURL(formData.url);
    const podcastTypeLabel = getPodcastTypeLabel();

    let referenceText = `${authorsForReference} (${dateFormatted}). ${formData.episodeTitle} ${podcastTypeLabel}. En <em>${formData.podcastTitle}</em>`;
    
    if (formData.platform) {
      referenceText += `. ${formData.platform}`;
    }
    
    if (url) {
      referenceText += `. ${url}`;
    } else {
      referenceText += '.';
    }

    const reference = {
      html: referenceText,
      text: referenceText.replace(/<[^>]*>/g, '')
    };

    setResult({ citation, reference });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 dark:text-gray-100">Episodio de pódcast</h1>
        <p className="text-gray-600 dark:text-gray-400">Episodios de pódcast</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6 dark:bg-gray-800 dark:border-gray-700">
          <AuthorFields
            authors={hosts}
            setAuthors={setHosts}
            authorType={hostType}
            setAuthorType={setHostType}
            label="Anfitriones"
            showGender={true}
          />

          <div>
            <label htmlFor="episodeTitle" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Título del episodio *
            </label>
            <input
              type="text"
              id="episodeTitle"
              name="episodeTitle"
              value={formData.episodeTitle}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              required
            />
          </div>

          <div>
            <label htmlFor="podcastTitle" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Título del pódcast *
            </label>
            <input
              type="text"
              id="podcastTitle"
              name="podcastTitle"
              value={formData.podcastTitle}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              required
            />
          </div>

          <div>
            <label htmlFor="podcastType" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Tipo de pódcast
            </label>
            <select
              id="podcastType"
              name="podcastType"
              value={formData.podcastType}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            >
              <option value="Audio">Audio</option>
              <option value="Video">Video</option>
            </select>
          </div>

          <div>
            <label htmlFor="platform" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Plataforma del pódcast
            </label>
            <input
              type="text"
              id="platform"
              name="platform"
              value={formData.platform}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              placeholder="ej: Spotify, Apple Podcasts"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Año *
              </label>
              <input
                type="number"
                id="year"
                name="year"
                value={formData.year}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              />
            </div>
            <div>
              <label htmlFor="day" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Día
              </label>
              <input
                type="number"
                id="day"
                name="day"
                value={formData.day}
                onChange={handleInputChange}
                min="1"
                max="31"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              />
            </div>
            <div>
              <label htmlFor="month" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Mes
              </label>
              <select
                id="month"
                name="month"
                value={formData.month}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              >
                <option value="">Seleccionar</option>
                <option value="01">Enero</option>
                <option value="02">Febrero</option>
                <option value="03">Marzo</option>
                <option value="04">Abril</option>
                <option value="05">Mayo</option>
                <option value="06">Junio</option>
                <option value="07">Julio</option>
                <option value="08">Agosto</option>
                <option value="09">Septiembre</option>
                <option value="10">Octubre</option>
                <option value="11">Noviembre</option>
                <option value="12">Diciembre</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              URL
            </label>
            <input
              type="url"
              id="url"
              name="url"
              value={formData.url}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
          </div>

          <button
            type="button"
            onClick={generateCitation}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Generar cita y referencia
          </button>
        </div>

        <div className="space-y-6">
          {!result && liveResult && (
            <CitationResult
              citation={liveResult.citation}
              reference={liveResult.reference}
              livePreview={true}
            />
          )}
          <CitationResult
            citation={result?.citation}
            reference={result?.reference}
          />
        </div>
      </div>
    </div>
  );
}

export default PodcastForm;